import { Button, ButtonProps } from "@mui/material";
import React, { ReactNode } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { IconComponent } from "../../icon-component";
import styles from "./CustomButton.module.css";
import { getColorOnCondition } from "../utils";
import { getFilteredDataAttributes } from "../../../utils";

type IconSize = React.ComponentProps<typeof IconComponent>["size"];
const iconSizes: Record<ButtonSize, IconSize> = {
	xs: "xs",
	sm: "xs",
	md: "sm",
	lg: "lg",
	xl: "xl",
};

type ButtonSize = "xs" | "sm" | "md" | "lg" | "xl";
type SvgIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;
type IconSource = IconProp | SvgIconComponent;
type Color = "primary" | "secondary";
type DataAttributes = {
	[key: `data-${string}`]: string | number | undefined;
};

interface CustomButtonProps extends Omit<ButtonProps, "size" | "color" | "startIcon" | "endIcon">, DataAttributes {
	autoTranslate?: "yes" | "no";
	color?: Color;
	size?: ButtonSize;
	startIcon?: IconSource;
	startIconColor?: string;
	endIcon?: IconSource;
	endIconColor?: string;
}
interface BtnIcon {
	icon: IconSource;
	icC?: string;
}

const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
	(
		{
			autoTranslate = "yes",
			children,
			className = "",
			color = "primary",
			component = "button",
			disableRipple = false,
			disabled = false,
			endIcon = null,
			endIconColor = "",
			fullWidth = false,
			id = "",
			onClick,
			onMouseOver = undefined,
			onMouseLeave = undefined,
			size = "md",
			startIcon = null,
			startIconColor = "",
			style = undefined,
			type = "button",
			variant = "text",
			...dataAttributes
		},
		ref
	) => {
		const iconColorOnCondition = (icColor?: string): string =>
			getColorOnCondition({ iconColor: icColor, variant, color, disabled });
		const btnIcon = ({ icon, icC }: BtnIcon): ReactNode => (
			<IconComponent color={iconColorOnCondition(icC)} icon={icon} size={iconSizes[size]} />
		);

		const filteredDataAttributes = getFilteredDataAttributes(dataAttributes);

		return (
			<Button
				ref={ref}
				className={`${styles.btn} ${className}`}
				component={component}
				data-color={color}
				data-size={size}
				data-variant={variant}
				disabled={disabled}
				disableRipple={disableRipple}
				fullWidth={fullWidth}
				id={id}
				style={style}
				translate={autoTranslate}
				type={type}
				onClick={onClick}
				onMouseLeave={onMouseLeave}
				onMouseOver={onMouseOver}
				{...filteredDataAttributes}
			>
				{startIcon && btnIcon({ icon: startIcon, icC: startIconColor })}
				{children}
				{endIcon && btnIcon({ icon: endIcon, icC: endIconColor })}
			</Button>
		);
	}
);

export default CustomButton;
